'use client'

import {
  SelectInput,
  useAllFormFields,
  useDebounce,
  useField,
} from '@payloadcms/ui'
import { getSiblingData } from 'payload/shared'
import { useMemo } from 'react'

import type { OptionObject, SelectFieldClientProps } from 'payload'
import type { FC } from 'react'

type SelectWithRelationshipFieldProps = SelectFieldClientProps & {
  relationshipFieldPath: string
  relationshipFieldScope?:
    | 'block'
    | 'sibling'
    | 'questionRow'
    | 'shootingGameOptions'
  hasMany?: boolean
}

const SelectWithRelationshipField: FC<SelectWithRelationshipFieldProps> = ({
  path,
  field,
  relationshipFieldPath,
  relationshipFieldScope = 'block',
  hasMany,
}) => {
  const { value, setValue } = useField<string | string[]>({ path })
  const pathParts = useMemo(() => path.split('.'), [path])
  const [fields] = useAllFormFields()
  const blockData = useMemo(
    () => getSiblingData(fields, `${pathParts[0]}.${pathParts[1]}`),
    [fields, pathParts],
  )
  const directSiblingData = useMemo(
    () => getSiblingData(fields, pathParts.slice(0, -1).join('.')),
    [fields, pathParts],
  )
  const questionRowData = useMemo(() => {
    const questionsIndex = pathParts.indexOf('questions')

    if (questionsIndex === -1 || questionsIndex + 1 >= pathParts.length) {
      return null
    }

    const questionRowPath = pathParts.slice(0, questionsIndex + 2).join('.')

    return getSiblingData(fields, questionRowPath)
  }, [fields, pathParts])
  const relationshipData = useMemo(() => {
    if (relationshipFieldScope === 'sibling') return directSiblingData

    if (relationshipFieldScope === 'questionRow') {
      return questionRowData ?? blockData
    }

    if (relationshipFieldScope === 'shootingGameOptions') {
      return blockData.type === 'perQuestionOptions'
        ? directSiblingData
        : blockData
    }

    return blockData
  }, [blockData, directSiblingData, questionRowData, relationshipFieldScope])
  const debouncedRelationshipData = useDebounce(relationshipData, 500)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Need for optimization
  const options = useMemo(
    () =>
      Array.isArray(debouncedRelationshipData[relationshipFieldPath])
        ? debouncedRelationshipData[relationshipFieldPath]?.map((item) => {
            return {
              value: item.id,
              label: item.name,
            }
          })
        : [],
    [debouncedRelationshipData],
  )

  return (
    <SelectInput
      {...field}
      path={path}
      options={options}
      value={value}
      hasMany={hasMany}
      onChange={(e) => {
        if (hasMany) {
          if (!Array.isArray(e)) {
            setValue([])
            return
          }

          const ids = (e as OptionObject[]).map((item) => item.value)

          setValue(ids)
        } else {
          if (!e || Array.isArray(e)) {
            setValue('')
            return
          }

          const id = (e as OptionObject).value

          setValue(id)
        }
      }}
    />
  )
}

export default SelectWithRelationshipField
